import {Injectable} from '@angular/core';
import {catchError, finalize, shareReplay, switchMap} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {DataService} from 'shared/_services/data.service';
import {UserService} from '../../../projects/user/src/lib/user.service';
import {DocumentationOAuthOutDataDto} from 'app/_models/model/DocumentationOAuthOutDataDto';
import {Store} from '@ngrx/store';
import * as userActions from 'app/_actions/user.actions';
import {HttpResponse} from '@angular/common/http';
import {REVOKE_TOKEN} from 'shared/magic';
import {environment} from '../../environments/environment';
import {TabCountService} from 'shared/_services/tab-count.service';
import {AccessTokenRefresh} from '../../../projects/journal/src/_interface/access-token-refresh.interface';
import {EditModeService} from 'shared/_services/edit-mode.service';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private refreshTokenRequest: Observable<any> | null = null;

	constructor(
		private data: DataService,
		private user: UserService,
		private store: Store,
		private tabCountService: TabCountService,
		private editModeService: EditModeService
	) { }

	private revokeToken() {
		const url = `${environment.apiUrl}/api/users/logout-refresh-token`;
		const payload = JSON.stringify({
			message: 'Revoke token on a tab close/refresh',
			timestamp: new Date(),
			source: 'tab_close'
		});

		if (navigator.sendBeacon) {
			const success = navigator.sendBeacon(url, payload);
			if (success) {
				this.manageRevokeTokenFlag('set');
				return;
			}
		}

		try {
			const xhr = new XMLHttpRequest();
			xhr.open('POST', url, false);
			xhr.setRequestHeader('Content-Type', 'application/json');
			xhr.send(payload);
			if (xhr.status === 200) {
				this.manageRevokeTokenFlag('set');
			}
		} catch (error) {
		}
	}

	public handlePageBeforeunload(): void {
		this.editModeService.disableViaBeacon();
		if (!this.tabCountService.isLastTab()) {
			return;
		}
		this.revokeToken();
	}

	manageRevokeTokenFlag(manage: 'get' | 'set' | 'remove') {
		if (manage === 'set') {
			sessionStorage.setItem(REVOKE_TOKEN, 'true');
		} else if (manage === 'remove') {
			sessionStorage.removeItem(REVOKE_TOKEN);
		}
	}

	startUserAuth() {
		const path = 'api/users/authenticate';
		return this.data.get<DocumentationOAuthOutDataDto>(path).pipe(
			catchError(err => {
				return throwError(err);
			}),
			switchMap( async authData => {
				const {documentationAuthOutDataDto} = authData;
				this.user.user = {
					name: `${documentationAuthOutDataDto.user.firstName} ${documentationAuthOutDataDto.user.lastName}`,
					guid: documentationAuthOutDataDto.user.userId,
					clinicId: documentationAuthOutDataDto.clinic.clinicId,
					initials: documentationAuthOutDataDto.user.initials,
					profession: documentationAuthOutDataDto.user.professionId,
					jasminEnabled: documentationAuthOutDataDto.user.jasminEnabled
				};
				return authData;
			})
		);
	}

	refreshToken(): Observable<HttpResponse<any>> {
		if (!this.refreshTokenRequest) {
			this.refreshTokenRequest = this.data.post<AccessTokenRefresh>('api/users/refresh-token', {}, {withLoader: false}).pipe(
				finalize(() => {
					this.refreshTokenRequest = null;
				}),
				shareReplay(1)
			);
		}
		return this.refreshTokenRequest;
	}

	logout = () => this.store.dispatch(userActions.logout());
}
